import { ReactNode } from "react";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { HStack } from "@design-system/HStack/HStack";
import { Loader } from "@design-system/Loader/Loader";
import {
    PlainModal,
    StandardModalBody,
    StandardModalFooter,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { VStack } from "@design-system/VStack/VStack";
import styles from "./ScheduleOptionsModal.module.scss";

export const SCHEDULE_OPTIONS_BACK_LABEL = "Back";

interface ScheduleOptionsModalContent {
    title: string;
    /** The policy behind the schedule the title names; the body's first line. */
    policyLine?: string;
    body: string;
    /** A second paragraph, e.g. which selected rows a bulk action skips. */
    note?: string;
    /** Option cards, fields or a recap under the body; absent when there is nothing to show. */
    options?: ReactNode;
    confirmLabel: string;
    isConfirmDisabled: boolean;
}

interface ScheduleOptionsModalProps {
    show: boolean;
    /** Null while the proposal loads. */
    content: ScheduleOptionsModalContent | null;
    isSubmitting: boolean;
    /** Return the submit's promise so a second click is ignored while it runs. */
    onConfirm: () => void | Promise<void>;
    onHide: () => void;
    onExited: () => void;
}

/**
 * The lifecycle dialog that confirms an action and how its entries are
 * treated in one step: title, body, the option cards, then Back and the
 * confirm. Nothing posts until the confirm.
 */
export const ScheduleOptionsModal = ({
    show,
    content,
    isSubmitting,
    onConfirm,
    onHide,
    onExited,
}: ScheduleOptionsModalProps) => (
    <PlainModal show={show} onHide={onHide} onExited={onExited}>
        <StandardModalHeader>
            {content?.title ?? "Preparing…"}
        </StandardModalHeader>
        {content ? (
            <>
                <StandardModalBody className={styles.modalBody}>
                    <VStack gap={24}>
                        {content.policyLine && (
                            <p className={styles.body}>{content.policyLine}</p>
                        )}
                        <p className={styles.body}>{content.body}</p>
                        {content.note && (
                            <p className={styles.body}>{content.note}</p>
                        )}
                        {content.options}
                    </VStack>
                </StandardModalBody>
                <StandardModalFooter className={styles.footer}>
                    <HStack gap={8} justify="end">
                        <Button
                            type="button"
                            variant="secondary"
                            size="xl"
                            disabled={isSubmitting}
                            onClick={onHide}
                        >
                            {SCHEDULE_OPTIONS_BACK_LABEL}
                        </Button>
                        <ButtonWithLoader
                            variant="primary"
                            size="xl"
                            loading={isSubmitting}
                            disabled={content.isConfirmDisabled}
                            onClick={onConfirm}
                        >
                            {content.confirmLabel}
                        </ButtonWithLoader>
                    </HStack>
                </StandardModalFooter>
            </>
        ) : (
            <Loader />
        )}
    </PlainModal>
);
